<!--
  International Auctions — sale catalogue (F1.3, BXS-3627).

  Built-in control flow ONLY: no <ng-container>, no <ng-template>, and so
  nothing for the BXS-3584 template guard (`npm run lint:templates`) to catch.
  A table assembled out of <ng-container> wrappers is precisely the shape that
  guard exists to reject, so the whole grid below is @if / @for.

  Three details a reviewer will check:
    * the first and third column headers read `sale.house.lot_label` and
      `sale.house.consignor_label` — Hip / Consignor at Fasig-Tipton, Lot /
      Vendor at Arqana. They are DATA, never translation keys.
    * "Register to bid" points at the logged redirect, never at the house.
    * a withdrawn lot keeps every column: a muted dash per empty cell and the
      Withdrawn pill in the outcome column.
-->
<section class="ia-sale">
  @if (saleLoading) {
    <!-- Header-shaped placeholder. The not-found state must never flash here,
         and neither must a white block: 146px plus the band's own padding is
         the height the real header settles at. -->
    <div class="ia-sale__boot">
      <app-skeleton-loader
        type="custom"
        height="146px"
      ></app-skeleton-loader>
    </div>
  } @else if (sale) {
    <!-- =================== Co-branded sale header ===================
      The house's colour FILLS this band rather than edging it, so the four
      custom properties below carry a partner's whole livery and no stylesheet
      knows a partner hex. `brand` resolves them once — including checking that
      the stored ink is actually readable on the stored colour.
    -->
    <header
      class="ia-hero"
      [style.--ia-brand]="brand.color"
      [style.--ia-ink]="brand.ink"
      [style.--ia-accent]="brand.accent"
      [style.--ia-accent-ink]="brand.accentInk"
    >
      <div class="ia-hero__inner">
        <div class="ia-hero__identity">
          <span class="ia-hero__plaque" [class.is-bare]="brand.logoOnBrand">
            @if (sale.house.logo_url) {
              <img
                class="ia-hero__logo"
                [src]="sale.house.logo_url"
                [alt]="sale.house.name"
                decoding="async"
              />
            } @else {
              <span class="ia-hero__wordmark">{{ sale.house.name }}</span>
            }
          </span>

          <div class="ia-hero__headings">
            <!-- "Hosted by Tattersalls" — the house name is a proper noun and
                 is interpolated into the phrase, never translated itself. -->
            <span class="ia-hero__eyebrow">
              {{
                'international_auctions.sale.hosted_by'
                  | translate: { house: sale.house.name }
              }}
            </span>

            <h1 class="ia-hero__title">
              {{ saleName }}
            </h1>

            <!--
              One line of facts separated by a dot, per the mockup, instead of
              the labelled stack this replaced: on a filled band the small
              uppercase labels fought the eyebrow directly above them.
            -->
            <p class="ia-hero__meta">
              @for (fact of headerFacts; track fact.key; let last = $last) {
                <span class="ia-hero__fact" [class.ia-num]="fact.isNumeric">
                  {{ fact.text }}
                </span>
                @if (!last) {
                  <span class="ia-hero__sep" aria-hidden="true">&#183;</span>
                }
              }
            </p>

            <span class="ia-hero__status">
              <app-international-status-chip
                [status]="sale.status"
                [brandColor]="brand.color"
              ></app-international-status-chip>
            </span>
          </div>
        </div>

        @if (canRegister) {
          <div class="ia-hero__actions">
            <!--
              Never `sale.registration_url` directly. The redirect is the click
              ledger, and a registration click is counted apart from a lot click
              because it is the stronger intent signal of the two.

              A plain href, not a routerLink: the SERVER answers this path and
              302s, which needs a document navigation. See `registerHref`.
            -->
            <a
              class="ia-hero__cta"
              [href]="registerHref"
              target="_blank"
              rel="noopener"
            >
              <span>
                {{
                  'international_auctions.sale.register_to_bid_on'
                    | translate: { house: sale.house.name }
                }}
              </span>
              <span class="ia-hero__arrow" aria-hidden="true">&#8599;</span>
            </a>

            <!-- Says where the click goes BEFORE it is made. The domain is read
                 off the house's own website_url, not written down twice. -->
            @if (houseDomain) {
              <span class="ia-hero__note">
                {{
                  'international_auctions.sale.opens_in_new_tab'
                    | translate: { domain: houseDomain }
                }}
              </span>
            }
          </div>
        }
      </div>
    </header>

    <div class="ia-body">
      <!-- ================= Breadcrumb ================= -->
      <nav class="ia-crumbs">
        <a class="ia-crumbs__link" [routerLink]="indexCommands | localeLink">
          {{ 'international_auctions.title' | translate }}
        </a>
        <span class="ia-crumbs__sep" aria-hidden="true"></span>
        <a class="ia-crumbs__link" [routerLink]="houseCommands | localeLink">
          {{ sale.house.name }}
        </a>
        <span class="ia-crumbs__sep" aria-hidden="true"></span>
        <span class="ia-crumbs__current">{{ saleName }}</span>
      </nav>

      <!-- ================= Toolbar ================= -->
      <div class="ia-toolbar">
        <div class="ia-toolbar__count">
          <span class="ia-toolbar__total">
            <span class="ia-num">{{ resultCountLabel }}</span>
            {{ sale.house.lot_label_plural }}
          </span>
          <span class="ia-toolbar__range">
            {{
              'international_auctions.sale.showing'
                | translate: showingParams
            }}
          </span>
        </div>

        <div class="ia-toolbar__controls">
          <button
            type="button"
            class="ia-btn ia-btn--ghost"
            [class.is-on]="filtersOpen"
            (click)="toggleFilters()"
          >
            <span>{{ 'international_auctions.filters.toggle' | translate }}</span>
            @if (activeFilterCount > 0) {
              <span class="ia-btn__badge ia-num">{{ activeFilterCount }}</span>
            }
          </button>

          <div class="ia-viewswitch">
            <button
              type="button"
              class="ia-viewswitch__btn"
              [class.is-on]="catalogueView === 'table'"
              (click)="setView('table')"
            >
              {{ 'international_auctions.sale.view_table' | translate }}
            </button>
            <button
              type="button"
              class="ia-viewswitch__btn"
              [class.is-on]="catalogueView === 'cards'"
              (click)="setView('cards')"
            >
              {{ 'international_auctions.sale.view_cards' | translate }}
            </button>
          </div>
        </div>
      </div>

      <!-- ================= Filter rail ================= -->
      @if (filtersOpen) {
        <div class="ia-filters">
          <div class="ia-filters__grid">
            <!-- The lot-number label is the HOUSE's word for it: Hip, not Lot,
                 at Fasig-Tipton. Same field, same rule as the column header. -->
            <label class="ia-field">
              <span class="ia-field__label">{{ sale.house.lot_label }}</span>
              <input
                class="ia-field__input ia-num"
                type="text"
                inputmode="text"
                autocomplete="off"
                [value]="lotNumberText"
                (input)="onLotNumberInput($event)"
              />
            </label>

            <label class="ia-field ia-field--wide">
              <span class="ia-field__label">
                {{ 'international_auctions.sale.name_by_sire_ex_dam' | translate }}
              </span>
              <input
                class="ia-field__input"
                type="search"
                autocomplete="off"
                [value]="searchText"
                [placeholder]="
                  'international_auctions.sale.search_placeholder' | translate
                "
                (input)="onSearchInput($event)"
              />
            </label>

            <label class="ia-field">
              <span class="ia-field__label">
                {{ 'international_auctions.sale.sex' | translate }}
              </span>
              <select
                class="ia-field__select"
                (change)="onSelectFilter('sex', $event)"
              >
                <option value="" [selected]="sex === ''">
                  {{ 'international_auctions.filters.all' | translate }}
                </option>
                @for (option of filterOptions.sexes; track option) {
                  <option [value]="option" [selected]="sex === option">
                    {{ option }}
                  </option>
                }
              </select>
            </label>

            <label class="ia-field">
              <span class="ia-field__label">
                {{ 'international_auctions.sale.year_foaled' | translate }}
              </span>
              <select
                class="ia-field__select ia-num"
                (change)="onSelectFilter('year', $event)"
              >
                <option value="" [selected]="yearFoaled === ''">
                  {{ 'international_auctions.filters.all' | translate }}
                </option>
                @for (option of filterOptions.years; track option) {
                  <option
                    [value]="option"
                    [selected]="yearFoaled === option.toString()"
                  >
                    {{ option }}
                  </option>
                }
              </select>
            </label>

            <label class="ia-field">
              <span class="ia-field__label">
                {{ 'international_auctions.sale.horse_type' | translate }}
              </span>
              <select
                class="ia-field__select"
                (change)="onSelectFilter('type', $event)"
              >
                <option value="" [selected]="horseType === ''">
                  {{ 'international_auctions.filters.all' | translate }}
                </option>
                @for (option of filterOptions.horseTypes; track option) {
                  <option [value]="option" [selected]="horseType === option">
                    {{ option }}
                  </option>
                }
              </select>
            </label>

            <!-- Consignor at Tattersalls and Fasig-Tipton, Vendor at Arqana. -->
            <label class="ia-field ia-field--wide">
              <span class="ia-field__label">
                {{ sale.house.consignor_label }}
              </span>
              <select
                class="ia-field__select"
                (change)="onSelectFilter('consignor', $event)"
              >
                <option value="" [selected]="consignor === ''">
                  {{ 'international_auctions.filters.all' | translate }}
                </option>
                @for (option of filterOptions.consignors; track option) {
                  <option [value]="option" [selected]="consignor === option">
                    {{ option }}
                  </option>
                }
              </select>
            </label>

            <label class="ia-field">
              <span class="ia-field__label">
                {{ 'international_auctions.sale.sale_day' | translate }}
              </span>
              <select
                class="ia-field__select"
                (change)="onSelectFilter('day', $event)"
              >
                <option value="" [selected]="saleDay === ''">
                  {{ 'international_auctions.sale.all_days' | translate }}
                </option>
                @for (option of filterOptions.saleDays; track option) {
                  <option [value]="option" [selected]="saleDay === option">
                    {{ option }}
                  </option>
                }
              </select>
            </label>
          </div>

          @if (activeFilterCount > 0) {
            <div class="ia-filters__foot">
              <button
                type="button"
                class="ia-btn ia-btn--clear"
                (click)="clearFilters()"
              >
                {{ 'international_auctions.filters.clear' | translate }}
              </button>
            </div>
          }
        </div>
      }

      <!-- ================= Catalogue ================= -->
      @if (lotsLoading) {
        <div class="ia-results">
          @if (catalogueView === 'table') {
            <app-skeleton-loader
              type="table"
              [cols]="6"
              [rows]="8"
            ></app-skeleton-loader>
          } @else {
            <div class="ia-cards">
              @for (slot of skeletonCards; track slot) {
                <app-skeleton-loader
                  type="custom"
                  height="176px"
                ></app-skeleton-loader>
              }
            </div>
          }
        </div>
      } @else if (rows.length === 0) {
        <div class="ia-empty">
          <p class="ia-empty__title">
            {{ 'international_auctions.empty.lots' | translate }}
          </p>
          @if (activeFilterCount > 0) {
            <button
              type="button"
              class="ia-btn ia-btn--clear"
              (click)="clearFilters()"
            >
              {{ 'international_auctions.filters.clear' | translate }}
            </button>
          }
        </div>
      } @else if (catalogueView === 'table') {
        <!--
          The SCROLLER is this div, never the page body. At 390px the six
          columns overflow inside it and <body> stays put — a horizontally
          scrolling page is the failure this screen exists to avoid.
        -->
        <div class="ia-tablewrap" tabindex="0">
          <table class="ia-table">
            <caption class="ia-table__caption">
              {{ 'international_auctions.sale.catalogue' | translate }}
            </caption>
            <thead>
              <tr class="ia-table__head">
                <th
                  scope="col"
                  class="ia-th ia-th--lot"
                >
                  {{ sale.house.lot_label }}
                </th>
                <th
                  scope="col"
                  class="ia-th ia-th--title"
                >
                  {{
                    'international_auctions.sale.name_by_sire_ex_dam'
                      | translate
                  }}
                </th>
                <th
                  scope="col"
                  class="ia-th ia-th--consignor"
                >
                  {{ sale.house.consignor_label }}
                </th>
                <th
                  scope="col"
                  class="ia-th ia-th--type"
                >
                  {{ 'international_auctions.sale.horse_type' | translate }}
                </th>
                <th
                  scope="col"
                  class="ia-th ia-th--outcome"
                >
                  {{ 'international_auctions.lot.outcome' | translate }}
                </th>
                <th
                  scope="col"
                  class="ia-th ia-th--price"
                >
                  {{ 'international_auctions.sale.price' | translate }}
                </th>
                <!-- The star column. A visible header would be a word wider
                     than the control it labels, so the name is for screen
                     readers only. -->
                <th scope="col" class="ia-th ia-th--fav">
                  <span class="visually-hidden">{{
                    'international_auctions.my_list.column' | translate
                  }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              @for (row of rows; track row.id) {
                <tr class="ia-tr" [class.ia-tr--withdrawn]="row.isWithdrawn">
                  <td class="ia-td ia-td--lot">
                    <a
                      class="ia-td__lotlink ia-num"
                      [routerLink]="row.commands | localeLink"
                    >
                      {{ row.lotNumber }}
                    </a>
                  </td>

                  <td class="ia-td ia-td--title">
                    @if (row.title) {
                      <a
                        class="ia-td__titlelink"
                        [routerLink]="row.commands | localeLink"
                      >
                        {{ row.title }}
                      </a>
                    } @else {
                      <span class="ia-dash" aria-hidden="true">&#8212;</span>
                    }
                    @if (row.pedigree) {
                      <span class="ia-td__pedigree">{{ row.pedigree }}</span>
                    }
                  </td>

                  <td class="ia-td ia-td--consignor">
                    @if (row.consignor) {
                      <span class="ia-td__wrap">{{ row.consignor }}</span>
                    } @else {
                      <span class="ia-dash" aria-hidden="true">&#8212;</span>
                    }
                  </td>

                  <td class="ia-td ia-td--type">
                    @if (row.horseType) {
                      <span class="ia-td__wrap">{{ row.horseType }}</span>
                    } @else {
                      <span class="ia-dash" aria-hidden="true">&#8212;</span>
                    }
                  </td>

                  <!-- The outcome lives here and ONLY here. A price in the
                       next column never implies one. -->
                  <td class="ia-td ia-td--outcome">
                    @if (row.hasOutcome) {
                      <span
                        class="ia-status"
                        [class.ia-status--sold]="row.status === 'sold'"
                        [class.ia-status--not-sold]="row.status === 'not_sold'"
                        [class.ia-status--private]="
                          row.status === 'private_sale'
                        "
                        [class.ia-status--withdrawn]="row.isWithdrawn"
                      >
                        {{ row.statusKey | translate }}
                      </span>
                    } @else {
                      <span class="ia-dash" aria-hidden="true">&#8212;</span>
                    }
                  </td>

                  <td class="ia-td ia-td--price">
                    @if (row.price) {
                      <span class="ia-num ia-td__price">{{ row.price }}</span>
                    } @else {
                      <span class="ia-dash" aria-hidden="true">&#8212;</span>
                    }
                  </td>

                  <td class="ia-td ia-td--fav">
                    <app-international-favourite-button
                      [lotId]="row.id"
                      variant="row"
                    ></app-international-favourite-button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <div class="ia-cards">
          @for (row of rows; track row.id) {
            <!--
              The star is a SIBLING of the card's link, not a child of it.
              `<a>` may not contain interactive content; a button nested inside
              one is repaired differently by every engine and breaks tab order
              in all of them. The wrapper is the grid item, the link fills it,
              and the star floats over the corner.
            -->
            <div class="ia-lotcard-wrap">
            <a
              class="ia-lotcard"
              [class.ia-lotcard--withdrawn]="row.isWithdrawn"
              [routerLink]="row.commands | localeLink"
            >
              <span class="ia-lotcard__top">
                <span class="ia-lotcard__pill">
                  <span>{{ sale.house.lot_label }}</span>
                  <span class="ia-num">{{ row.lotNumber }}</span>
                </span>
                @if (row.hasOutcome) {
                  <span
                    class="ia-status"
                    [class.ia-status--sold]="row.status === 'sold'"
                    [class.ia-status--not-sold]="row.status === 'not_sold'"
                    [class.ia-status--private]="row.status === 'private_sale'"
                    [class.ia-status--withdrawn]="row.isWithdrawn"
                  >
                    {{ row.statusKey | translate }}
                  </span>
                }
              </span>

              @if (row.title) {
                <span class="ia-lotcard__title">{{ row.title }}</span>
              }
              @if (row.pedigree) {
                <span class="ia-lotcard__pedigree">{{ row.pedigree }}</span>
              }

              <span class="ia-lotcard__facts">
                <span class="ia-lotcard__row">
                  <span class="ia-lotcard__key">
                    {{ sale.house.consignor_label }}
                  </span>
                  @if (row.consignor) {
                    <span class="ia-lotcard__val">{{ row.consignor }}</span>
                  } @else {
                    <span class="ia-dash" aria-hidden="true">&#8212;</span>
                  }
                </span>

                <span class="ia-lotcard__row">
                  <span class="ia-lotcard__key">
                    {{ 'international_auctions.sale.horse_type' | translate }}
                  </span>
                  @if (row.horseType) {
                    <span class="ia-lotcard__val">{{ row.horseType }}</span>
                  } @else {
                    <span class="ia-dash" aria-hidden="true">&#8212;</span>
                  }
                </span>

                <span class="ia-lotcard__row">
                  <span class="ia-lotcard__key">
                    {{ 'international_auctions.sale.price' | translate }}
                  </span>
                  @if (row.price) {
                    <span class="ia-lotcard__val ia-num">{{ row.price }}</span>
                  } @else {
                    <span class="ia-dash" aria-hidden="true">&#8212;</span>
                  }
                </span>
              </span>
            </a>

              <app-international-favourite-button
                [lotId]="row.id"
                variant="card"
              ></app-international-favourite-button>
            </div>
          }
        </div>
      }

      <!--
        Rebuilt with each page, so `updatePages` recomputes the window: 1,580
        lots at 24 a page is 66 pages, which this control renders as at most
        seven buttons with ellipses — and the last page stays one click away.
      -->
      @if (!lotsLoading && lots && lots.total_pages > 1) {
        <div class="ia-pager">
          <app-pagination
            [totalPages]="lots.total_pages"
            [currentPage]="lots.page"
            [isCardPagination]="true"
            (pageChanged)="onPageChanged($event)"
          ></app-pagination>
        </div>
      }

      <!-- ================= Preview-only footer ================= -->
      <footer class="ia-disclaimer">
        <span class="ia-disclaimer__badge">
          {{ 'international_auctions.preview_only' | translate }}
        </span>
        <span class="ia-disclaimer__text">
          {{ 'international_auctions.disclaimer' | translate }}
        </span>
      </footer>
    </div>
  } @else {
    <!-- Unknown sale slug. `getSale()` answers null; it never throws. -->
    <div class="ia-body">
      <div class="ia-notfound">
        <h1 class="ia-notfound__title">
          {{ 'international_auctions.empty.sale_not_found' | translate }}
        </h1>
        <p class="ia-notfound__hint">
          {{ 'international_auctions.empty.sale_not_found_hint' | translate }}
        </p>
        <a class="ia-btn ia-btn--solid" [routerLink]="houseCommands | localeLink">
          {{ 'international_auctions.card.view_sales' | translate }}
        </a>
      </div>

      <footer class="ia-disclaimer">
        <span class="ia-disclaimer__badge">
          {{ 'international_auctions.preview_only' | translate }}
        </span>
        <span class="ia-disclaimer__text">
          {{ 'international_auctions.disclaimer' | translate }}
        </span>
      </footer>
    </div>
  }
</section>
